import React from 'react';
import { Badge, Box } from '@mui/material';

/**
 * [Web UX 8 (jelvény-javítás), 2026-10-09] SOR KÖZBENI (inline) vizuálisan rejtett szöveg az akadálymentes névhez.
 *
 * Miért nem a MUI `visuallyHidden` mintája (a korábbi `VISUALLY_HIDDEN`, `position: absolute`): az abszolút elemet a böngésző blokk-szintűvé teszi, és az
 * akadálymentes név számításakor a blokk-elem KÖRÉ szóközt szúr → a fül neve „Beállítások , 11 teendő vár" lett (szóköz a
 * vessző előtt; 1. validátor-kör 3. lelet). MÉRVE Chromiumban (CDP `Accessibility.getFullAXTree`) és a Playwright névszámításában
 * is: az abszolút rejtett span minden elrendezésben „Beállítások , …"-t ad, a soron belüli `font-size: 0` span „Beállítások, …"-t.
 * A `font-size: 0` + `line-height: 0` + `letter-spacing: 0` + `word-spacing: 0` a szöveget 0 szélességűvé teszi (nincs rés a
 * címke után), a szöveg az akadálymentességi fában bent marad (a név tartalmazza).
 *
 * [Web UX 8 (jelvény-javítás), 2026-10-09] A `letter-spacing: 0` NEM elhagyható (2. validátor-kör 2. lelet): a MUI Tab
 * tipográfiája ABSZOLÚT betűközt ad (`letter-spacing: 0.39998px`), ami a 0 betűméret mellett is karakterenként hozzáadódik →
 * MÉRVE Chromiumban a 16 karakteres rejtett span 6 px széles volt (rés a címke és a jelvény között). A `word-spacing: 0` ugyanezért
 * (abszolút szóköz-többlet örökölhető). Őr: Jest `PendingCountBadge.test.tsx` (stílus) + E2E `flow-admin-pending-badges.spec.ts`
 * (a rejtett span valódi szélessége < 0,5 px).
 */
export const VISUALLY_HIDDEN_INLINE = {
  // kifejezetten `inline` (a névszámítás a nem-`inline` elem köré szóközt tesz; a jsdom alapértéke sem mindig `inline`)
  display: 'inline',
  fontSize: 0,
  lineHeight: 0,
  letterSpacing: 0,
  wordSpacing: 0,
  color: 'transparent',
  whiteSpace: 'nowrap',
} as const;

/**
 * [Web UX 8 (jelvény-javítás), 2026-10-09] A „jóváhagyásra váró" darabszám jelvénye a fül CÍMKÉJE UTÁN, a SORON BELÜL
 * (statikus pozícióval), nem a címke/ikon sarkára abszolút pozícióval kilógatva.
 *
 * Miért: élesben (admin, sötét téma; a user képernyőképe) a Magánóra- és a Regisztráció-al-fülön NEM volt jelvény, az
 * Asztalfoglalások al-fülén „10" — tehát a függő összeg 10 volt (0 + 10 + 0), a felső „Beállítások" fül jelvényének „10"-et
 * kellett volna mutatnia, de abból csak az „1" látszott: a „0"-t a fül levágta. MÉRVE lokálisan (a javítás előtt, 1280 px,
 * kétjegyű szám mellett — a teszt-forgatókönyv 10 + 1 = „11"): a jelvény jobb széle 1065,5 px, a fül (`Tab`, `overflow: hidden`)
 * jobb széle 1052,9 px → 12,6 px levágva, a második számjegy eltűnt. Ok: a régi `Dashboard.tsx:799` (HEAD `214aca5`)
 * `sx={{ '& .MuiBadge-badge': { top: 2, right: -16 } }}` a fülön KÍVÜLRE tolta a jelvényt. Az al-fülön
 * (`SettingsManagement.tsx` régi `withBadge`, az IKONON) a jelvény a címke első betűjére lógott, és kisebb fül-magasságnál
 * a fül teteje vágta. A `.MuiTabs-scroller` is vág (görgetés miatt `overflow`), ezért a megoldás NEM `overflow: visible`,
 * hanem a soron belüli elhelyezés: a jelvény így mindig a fül saját dobozában marad, és a „99+" is kifér.
 *
 * Ez a komponens CSAK a látható számot rajzolja (`aria-hidden`); a képernyőolvasónak szóló „, N teendő vár" szöveget a
 * `labelWithPendingBadge` teszi közvetlenül a címke-szöveg után (lásd ott). 0-nál és ismeretlennél (`null`) NINCS jelvény.
 */
interface PendingCountBadgeProps {
  /** A darabszám; `null`/`undefined` = ismeretlen → nincs jelvény. */
  count: number | null | undefined;
  /** A jelvény (a szám) `data-testid`-je. */
  testId: string;
}

const hasPending = (count: number | null | undefined): count is number =>
  count !== null && count !== undefined && count > 0;

export const PendingCountBadge: React.FC<PendingCountBadgeProps> = ({ count, testId }) => {
  if (!hasPending(count)) return null;
  return (
    <Badge
      badgeContent={count}
      color="error"
      max={99}
      sx={{
        ml: 1,
        flexShrink: 0,
        verticalAlign: 'middle',
      }}
      // Soron belül: a MUI alapból `position: absolute` + `translate(50%, -50%)`-tel a gyermek sarkára teszi. Inline
      // stílussal írjuk felül (a legerősebb szabály — téma/sx-sorrendtől független, és jsdom-ban is ellenőrizhető).
      slotProps={{
        badge: {
          'aria-hidden': true,
          'data-testid': testId,
          style: { position: 'static', transform: 'none' },
        } as React.HTMLAttributes<HTMLSpanElement>,
      }}
    />
  );
};

/**
 * Fül-címke a jelvénnyel: a szöveg és a jelvény egy sorban, tördelés nélkül. 0/ismeretlen esetén a puszta címke.
 *
 * Akadálymentesség: a szám `aria-hidden`; a rejtett „, N teendő vár" a címke-szöveggel EGY soron belüli `span`-ban, közvetlenül
 * utána áll (nem a flex-tároló külön gyermekeként, nem abszolút pozícióval), így a fül neve „Beállítások, 11 teendő vár"
 * (szóköz nélkül a vessző előtt). Nem `aria-label`, mert az felülírná a fül nevét.
 */
export const labelWithPendingBadge = (label: React.ReactNode, count: number | null | undefined, testId: string): React.ReactNode =>
  hasPending(count) ? (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
      <span>
        {label}
        <Box component="span" sx={VISUALLY_HIDDEN_INLINE}>
          {`, ${count} teendő vár`}
        </Box>
      </span>
      <PendingCountBadge count={count} testId={testId} />
    </Box>
  ) : (
    label
  );

export default PendingCountBadge;
